/**
 * The card object: a lit, normal-mapped quad, its shadow, and the pool that
 * recycles both.
 *
 * A card is a `Mesh` rather than a `Sprite` because the interesting parts all
 * need the vertex stage. `filters/cardLighting.ts` lights it per-pixel against
 * `LIGHT`, and collapses the card's lift, its turn and its travel lean into one
 * perspective divide — so a flip is a genuine trapezoid with perspective-correct
 * texturing rather than a rectangle squashed to zero width.
 *
 * The **shadow is derived from the same LIGHT**. Its direction is the direction
 * away from the light *at that card's position on the table*, its length comes
 * from the card's virtual lift through `SHADOW.offsetScale`, and its blur and
 * alpha grow and weaken with lift by crossfading two baked stamps. Shading and
 * shadow are computed from one set of numbers, so they cannot disagree.
 *
 * ## Contact, and why a card looks like it is touching the felt
 *
 * A cast shadow alone is not enough. Real objects also carry a hard, small,
 * very dark occlusion right where they meet a surface, and that patch behaves
 * *inversely* to height: it tightens and darkens as the object settles and
 * spreads and fades as it lifts away. `CardShadow` therefore carries a third
 * stamp — a tight, near-black contact patch hugging the card's lower edge,
 * driven off the same lift the cast shadow is driven off, on an exponential
 * that has all but died by the time a card is in flight.
 *
 * The same thing happens **between cards**. Every frame `updateCards` finds,
 * for each card, the nearest card drawn above it, and hands the shader that
 * card as a rounded box in the receiver's own frame — already pushed away from
 * the light by the gap between the two on exactly the offset law the drop
 * shadow uses. Two cards resting on one another give no offset and a hard black
 * crevice; a card flying overhead gives a long offset and a soft grey pool. One
 * uniform covers contact occlusion in a pile *and* the shadow of a card in
 * flight falling across the cards it passes over.
 *
 * Shadows are separate display objects: they belong on the `shadows` layer,
 * under the felt's card layer. `CardPool` will parent them for you if you hand
 * it the layers; otherwise parent `sprite.shadow` yourself.
 *
 * **`updateCards(dt)` must be called once per frame from the render loop.** It
 * is what feeds each card's screen position into its light vector, advances the
 * foil sweep and the reveal envelopes, derives the travel tilt, the motion blur
 * and the travel sheen from motion, resolves inter-card occlusion, and keeps
 * every shadow glued to its card. Without it the cards render, but they render
 * as if frozen at the origin.
 *
 * Every effect added on top of the base lighting is tunable through `CARD_FX`
 * and switches off at zero — the shader branches on the uniform rather than
 * running the maths and multiplying by nothing.
 *
 * Positions are read from `x` / `y` in **design units** — the coordinate space
 * `layout.ts` is written in. Cards are expected to be direct children of a
 * layer whose own transform is the letterbox scale and nothing else.
 */

import { Container, ImageSource, Mesh, Sprite, Texture, type MeshGeometry } from 'pixi.js';

import { elevation, palette } from '@/core/tokens';
import { type CardId } from '@/game/types';
import { CARD, DESIGN, LIGHT, SHADOW } from '@/pixi/layout';
import {
  getBackTexture,
  getBackUV,
  getFaceUV,
  getNormalUV,
  getShadowStamps,
} from '@/pixi/cardTexture';
import {
  CARD_SHADING,
  CardLightingShader,
  getCardGeometry,
} from '@/pixi/filters/cardLighting';
import { LIFT } from '@/motion/easing';
import { FoilFilter, isWildCard, prefersReducedMotion } from '@/pixi/filters/foil';

/* ------------------------------------------------------------------ *
 * Tuning
 * ------------------------------------------------------------------ */

export const CARD_PHYSICS = {
  /**
   * The card's own thickness, in design units. A card "flat on the felt" is not
   * at height zero — it still sits on its own stock — so it still throws a
   * short contact shadow instead of hiding perfectly beneath itself.
   */
  thickness: 5,
  /** Lift at which the shadow has faded to roughly half strength. */
  shadowFalloff: 115,
  /** Design units per second of travel that produce one unit of surface slope. */
  tiltPerVelocity: 0.000075,
  /** Hard cap on the travel lean. Past this it stops reading as a card. */
  maxTilt: 0.2,
  /** Exponential smoothing rate for the travel lean, per second. */
  tiltSmoothing: 14,
  /** Specular multiplier applied to a wild's lit pass. */
  wildGloss: 1.3,
  /** Baseline foil strength for a wild that is not the active card. */
  wildIdleIntensity: 0.82,
  /** Foil strength while the wild is the active card. */
  wildActiveIntensity: 1.45,
} as const;

/**
 * The lift a card lying in a pile actually sits at.
 *
 * `elevation.resting` is expressed as a shadow blur, so running it back through
 * the shadow model is the one honest way to say "settled" — change `elevation`
 * or `SHADOW` and everything that keys off contact follows.
 */
const REST_LIFT =
  SHADOW.blurPerLift > 0
    ? Math.max(0, (elevation.resting.blur - SHADOW.blurBase) / SHADOW.blurPerLift)
    : 0;

/** Contact darkening where a card meets the felt. */
export interface ContactFX {
  /** Peak darkening of the patch, 0..1. `0` switches it off entirely. */
  strength: number;
  /** Lift above resting, in design units, over which it fades e-fold. */
  falloff: number;
  /** How far past the cast shadow the patch sits, in design units. */
  offset: number;
  /** Fractional spread per design unit of excess lift. */
  spread: number;
}

/** Contact darkening a card takes from the card drawn above it. */
export interface OcclusionFX {
  /** Peak darkening in the crevice, 0..1. `0` switches it off entirely. */
  strength: number;
  /** Penumbra radius at zero gap, in design units. */
  radius: number;
  /** Penumbra growth per design unit of gap between the two cards. */
  spread: number;
  /** Gap, in design units, over which the darkening fades e-fold. */
  falloff: number;
  /**
   * Cards fainter than this are too transparent to occlude anything. Keep it
   * low: the strength already scales with the occluder's alpha, so a high cut
   * would pop the crevice off as a pile fades out rather than dissolve it.
   */
  minAlpha: number;
}

/** Directional blur taken along a card's own travel. */
export interface MotionBlurFX {
  /** Overall strength, 0..1. `0` switches it off — a still card is untouched. */
  strength: number;
  /** Travel per frame, in design units, below which nothing is blurred. */
  threshold: number;
  /** Longest smear, in design units. Stops a cut or a fast-forward streaking. */
  maxLength: number;
}

/** The lit sliver of card stock standing along the card's far edge. */
export interface EdgeFX {
  /** Width for a card lying at rest, in design units. `0` switches it off. */
  width: number;
  /** Extra width per design unit of lift. */
  liftGain: number;
  /** Ceiling on the width, in design units. */
  maxWidth: number;
  /** Brightness multiplier. `0` switches it off. */
  gain: number;
}

/** The anisotropic highlight every card catches as it moves. */
export interface SheenFX {
  /** Peak specular added at full drive. `0` switches it off. */
  gain: number;
  /** Travel speed, design units per second, that reaches full drive. */
  refSpeed: number;
  /** Spin, radians per second, that reaches full drive. */
  refSpin: number;
  /** Turn rate, flip units per second, that reaches full drive. */
  refFlip: number;
  /** How far the lobe stretches along travel, 0..1. `0` is isotropic. */
  anisotropy: number;
  /** The share of it a wild keeps, so it never fights the foil sweep. */
  wildShare: number;
}

export interface CardFX {
  contact: ContactFX;
  occlusion: OcclusionFX;
  motionBlur: MotionBlurFX;
  edge: EdgeFX;
  sheen: SheenFX;
}

/**
 * Live tuning for everything layered on top of the base lighting.
 *
 * Mutable on purpose: every field is read fresh each frame, and every effect
 * costs nothing at zero because the shader branches on the uniform rather than
 * evaluating the term and multiplying it out.
 */
export const CARD_FX: CardFX = {
  contact: {
    strength: 0.52,
    falloff: 19,
    offset: 3.4,
    spread: 0.004,
  },
  occlusion: {
    strength: 0.7,
    radius: 6.5,
    spread: 0.42,
    falloff: 44,
    minAlpha: 0.06,
  },
  motionBlur: {
    strength: 1,
    threshold: 1.2,
    maxLength: 24,
  },
  edge: {
    /**
     * Baseline width, in design units. The geometric term is added on top.
     *
     * Kept close to the geometric truth. The projected width of a cut edge is
     * 0.19 units at the centre pile and 0.73 at the outer ones, and inflating
     * that to ~2.8 turned a thickness cue into a dark band down one side of
     * every card — the edge stopped reading as stock and started reading as a
     * smudge, particularly on cards left of centre where it lands on the side
     * the key is lighting.
     */
    width: 0.35,
    /**
     * Extra width per design unit of lift.
     *
     * Now additive in design units rather than a multiplier. Lift moves the
     * card toward the eye and widens the edge by VIEW_DIST/(VIEW_DIST - lift),
     * which is 1.039 at lift 90 — the previous 0.019 multiplier produced 2.71,
     * roughly forty times the geometric value.
     */
    liftGain: 0.01,
    maxWidth: 1.8,
    gain: 1,
  },
  sheen: {
    gain: 0.32,
    refSpeed: 1700,
    refSpin: 6,
    refFlip: 2.4,
    anisotropy: 0.72,
    wildShare: 0.35,
  },
};

/** Shallow-merge a partial tuning over `CARD_FX`. Safe to call at any time. */
export function configureCardFX(options: {
  contact?: Partial<ContactFX>;
  occlusion?: Partial<OcclusionFX>;
  motionBlur?: Partial<MotionBlurFX>;
  edge?: Partial<EdgeFX>;
  sheen?: Partial<SheenFX>;
}): void {
  if (options.contact) Object.assign(CARD_FX.contact, options.contact);
  if (options.occlusion) Object.assign(CARD_FX.occlusion, options.occlusion);
  if (options.motionBlur) Object.assign(CARD_FX.motionBlur, options.motionBlur);
  if (options.edge) Object.assign(CARD_FX.edge, options.edge);
  if (options.sheen) Object.assign(CARD_FX.sheen, options.sheen);
}

/* ------------------------------------------------------------------ *
 * Contact stamp
 * ------------------------------------------------------------------ */

/**
 * Blur of the baked contact patch, in design units — half the tightest blur the
 * cast shadow can reach. This is the hard core of the occlusion, the part the
 * penumbra has not swallowed yet, so it has to be crisper than anything
 * `cardTexture` bakes for the cast shadow.
 */
const CONTACT_BLUR = SHADOW.blurBase * 0.5;

/** Low enough to be invisible, high enough not to be culled. */
const WARM_ALPHA = 0.002;

/** Card width in texture pixels used when baking the contact patch. */
const CONTACT_CARD_PX = 256;

interface ContactStamp {
  readonly texture: Texture;
  /** Size of the stamp expressed in design units. */
  readonly width: number;
  readonly height: number;
}

let contactStamp: ContactStamp | null = null;

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w * 0.5, h * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

/**
 * Bake the contact patch, once. Same trick `cardTexture` uses for the cast
 * stamps: the shape is drawn far off-canvas and only its shadow lands in frame,
 * because `shadowBlur` is the one blur primitive every browser has always had.
 */
function getContactStamp(): ContactStamp {
  if (contactStamp) return contactStamp;
  if (typeof document === 'undefined') {
    throw new Error('card: the contact shadow needs a DOM to bake');
  }

  const res = CONTACT_CARD_PX / CARD.width;
  const cardW = CONTACT_CARD_PX;
  const cardH = Math.round(CARD.height * res);
  const blur = CONTACT_BLUR * res;
  const pad = Math.ceil(blur * 2.4 + 4);

  const canvas = document.createElement('canvas');
  canvas.width = cardW + pad * 2;
  canvas.height = cardH + pad * 2;
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) throw new Error('card: 2D canvas context unavailable');

  const ink = `rgb(${(palette.void >> 16) & 0xff}, ${(palette.void >> 8) & 0xff}, ${palette.void & 0xff})`;
  const offset = canvas.width * 4;
  ctx.shadowColor = ink;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetX = offset;
  ctx.fillStyle = ink;
  roundRectPath(ctx, pad - offset, pad, cardW, cardH, CARD.cornerRadius * res);
  ctx.fill();

  const source = new ImageSource({
    resource: canvas,
    label: 'card-contact-shadow',
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
  });
  contactStamp = {
    texture: new Texture({ source, label: 'card-contact-shadow' }),
    width: canvas.width / res,
    height: canvas.height / res,
  };
  return contactStamp;
}

/* ------------------------------------------------------------------ *
 * Shadow
 * ------------------------------------------------------------------ */

/**
 * A card's drop shadow: two baked blur levels crossfaded by lift so a card in
 * the air really does cast a softer shadow rather than just a bigger one, plus
 * a tight contact patch that runs the other way and dies as the card leaves the
 * felt.
 *
 * The container sits exactly on the card and each stamp carries its own offset,
 * because the contact patch hugs a different distance than the cast shadow
 * does. All three offsets point the same way — away from `LIGHT` — so nothing
 * here can disagree with the shading.
 */
export class CardShadow extends Container {
  readonly #tight: Sprite;
  readonly #soft: Sprite;
  readonly #contact: Sprite;
  readonly #tightBlur: number;
  readonly #softBlur: number;
  readonly #tightSX: number;
  readonly #tightSY: number;
  readonly #softSX: number;
  readonly #softSY: number;
  readonly #contactSX: number;
  readonly #contactSY: number;

  constructor() {
    super({ label: 'card-shadow' });
    // Multiply, not alpha: on a plum-black felt an alpha-blended near-black
    // stamp barely registers, while multiplying darkens the felt the way an
    // occluder actually does. It is a native GL blend, so no back buffer.
    this.blendMode = 'multiply';
    const { tight, soft } = getShadowStamps();
    const contact = getContactStamp();

    this.#tight = new Sprite({ texture: tight.texture, anchor: 0.5 });
    this.#tight.width = tight.width;
    this.#tight.height = tight.height;
    this.#tightSX = this.#tight.scale.x;
    this.#tightSY = this.#tight.scale.y;

    this.#soft = new Sprite({ texture: soft.texture, anchor: 0.5 });
    this.#soft.width = soft.width;
    this.#soft.height = soft.height;
    this.#softSX = this.#soft.scale.x;
    this.#softSY = this.#soft.scale.y;

    this.#contact = new Sprite({ texture: contact.texture, anchor: 0.5 });
    this.#contact.width = contact.width;
    this.#contact.height = contact.height;
    this.#contactSX = this.#contact.scale.x;
    this.#contactSY = this.#contact.scale.y;
    this.#contact.visible = false;

    this.#tightBlur = tight.blur;
    this.#softBlur = soft.blur;

    this.addChild(this.#soft, this.#tight, this.#contact);
  }

  /**
   * Place and shape the shadow for a card at `x, y` in design units.
   *
   * Direction and length come from `LIGHT` and the card's height; blur comes
   * from `SHADOW.blurBase` and `SHADOW.blurPerLift`; alpha from
   * `SHADOW.alphaBase`, weakening as the card climbs away from the felt. The
   * contact patch runs inversely to all of it.
   */
  sync(
    x: number,
    y: number,
    rotation: number,
    scaleX: number,
    scaleY: number,
    lift: number,
    alpha: number,
  ): void {
    const height = CARD_PHYSICS.thickness + Math.max(lift, 0);

    // Away from the light, and how far away depends on where on the table the
    // card is — the same positional term the shader lights with.
    let dx = x - LIGHT.x;
    let dy = y - LIGHT.y;
    const distance = Math.hypot(dx, dy);
    if (distance > 1e-4) {
      dx /= distance;
      dy /= distance;
    } else {
      dx = 0;
      dy = 1;
    }

    this.position.set(x, y);
    this.rotation = rotation;
    const sx = Math.abs(scaleX) > 1e-4 ? scaleX : 1e-4;
    const sy = Math.abs(scaleY) > 1e-4 ? scaleY : 1e-4;
    this.scale.set(sx, sy);

    // The away-from-light direction expressed in this container's own frame, so
    // a stamp offset by `n` along it lands exactly `n` design units away on the
    // table however the card is turned or squashed.
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const lx = (dx * cos + dy * sin) / sx;
    const ly = (-dx * sin + dy * cos) / sy;

    // Closer to the light means a larger cast shadow — straight out of LIGHT.z.
    const spread = LIGHT.z / Math.max(LIGHT.z - lift, LIGHT.z * 0.35);
    const cast = height * SHADOW.offsetScale;

    const blur = SHADOW.blurBase + SHADOW.blurPerLift * Math.max(lift, 0);
    const mix = clamp((blur - this.#tightBlur) / (this.#softBlur - this.#tightBlur), 0, 1);

    const fade = 1 / (1 + Math.max(lift, 0) / CARD_PHYSICS.shadowFalloff);
    const density = clamp(SHADOW.alphaBase * fade * alpha, 0, 0.995);

    // Two multiplied layers compound, so a plain crossfade would go pale in the
    // middle. Splitting the transmission geometrically keeps total darkness
    // exactly `density` at every blur level.
    const transmission = 1 - density;
    this.#tight.alpha = 1 - Math.pow(transmission, 1 - mix);
    this.#soft.alpha = 1 - Math.pow(transmission, mix);
    this.#tight.position.set(lx * cast, ly * cast);
    this.#soft.position.set(lx * cast, ly * cast);
    this.#tight.scale.set(this.#tightSX * spread, this.#tightSY * spread);
    this.#soft.scale.set(this.#softSX * spread, this.#softSY * spread);
    this.alpha = 1;

    // The contact patch. Small radius, high opacity, hugging the lower edge, and
    // driven INVERSELY by lift: full and tight while the card is settled, spread
    // and gone by the time it is in flight. That inverse is the whole effect —
    // it is what makes a card visibly touch down rather than arrive.
    const contact = CARD_FX.contact;
    const excess = Math.max(0, lift - REST_LIFT);
    const settle = contact.falloff > 0 ? Math.exp(-excess / contact.falloff) : 0;
    const core = clamp(contact.strength * settle * alpha, 0, 0.995);
    if (core > 0.003) {
      const reach = cast + contact.offset;
      const grow = spread * (1 + excess * contact.spread);
      this.#contact.visible = true;
      this.#contact.alpha = core;
      this.#contact.position.set(lx * reach, ly * reach);
      this.#contact.scale.set(this.#contactSX * grow, this.#contactSY * grow);
    } else {
      this.#contact.visible = false;
    }
  }

  reset(): void {
    this.position.set(0, 0);
    this.rotation = 0;
    this.scale.set(1, 1);
    this.alpha = 1;
    this.visible = true;
    this.#tight.alpha = 0;
    this.#soft.alpha = 0;
    this.#tight.position.set(0, 0);
    this.#soft.position.set(0, 0);
    this.#contact.alpha = 0;
    this.#contact.position.set(0, 0);
    this.#contact.visible = false;
  }
}

/* ------------------------------------------------------------------ *
 * Card
 * ------------------------------------------------------------------ */

/**
 * Every sprite currently being ticked, as a set for membership and as an array
 * for iteration. The array exists so the per-frame passes can index rather than
 * allocate a fresh iterator sixty times a second.
 */
const liveCards = new Set<CardSprite>();
const liveList: CardSprite[] = [];

function trackCard(card: CardSprite): void {
  if (liveCards.has(card)) return;
  liveCards.add(card);
  liveList.push(card);
}

function untrackCard(card: CardSprite): void {
  if (!liveCards.delete(card)) return;
  const index = liveList.indexOf(card);
  if (index >= 0) liveList.splice(index, 1);
}

/** `cardId` when the sprite is not showing anything yet. */
const NO_CARD = '';

export class CardSprite extends Container {
  /**
   * The card's drop shadow. It is **not** a child of this container — it lives
   * on the `shadows` layer so every shadow renders under every card. `CardPool`
   * parents it when given a shadow layer; otherwise parent it yourself.
   */
  readonly shadow: CardShadow;

  readonly #mesh: Mesh<MeshGeometry, CardLightingShader>;
  readonly #shader: CardLightingShader;
  #foil: FoilFilter | null = null;

  #cardId: CardId = NO_CARD;
  #wild = false;
  #wildActive = false;
  #flip = 0;
  #lift = 0;
  #glow = 0;
  #showingFace = false;
  #squashFromFlip = true;

  #lastX = Number.NaN;
  #lastY = Number.NaN;
  #lastRotation = Number.NaN;
  #lastFlip = 0;
  #tiltX = 0;
  #tiltY = 0;
  /** Smoothed table-space velocity, in design units per second. */
  #velX = 0;
  #velY = 0;
  /** Smoothed angular rates: radians per second, and flip units per second. */
  #spin = 0;
  #flipRate = 0;

  #destroyed = false;

  constructor() {
    super({ label: 'card' });

    this.#shader = new CardLightingShader();
    this.#shader.setBackRect(getBackUV());
    this.#shader.setFaceRect(getBackUV());
    this.#shader.setNormalRect(getNormalUV('back'));

    this.#mesh = new Mesh<MeshGeometry, CardLightingShader>({
      geometry: getCardGeometry(),
      shader: this.#shader,
      texture: getBackTexture(),
      label: 'card-mesh',
    });
    this.addChild(this.#mesh);

    this.shadow = new CardShadow();
    trackCard(this);
  }

  /* -------------------------------------------------- state */

  /** The card currently shown, or `''` when the sprite is blank. */
  get cardId(): CardId {
    return this.#cardId;
  }

  /** True for J♠ and J♣. J♥ and J♦ report false — they are ordinary tens. */
  get isWild(): boolean {
    return this.#wild;
  }

  /** True once the turn has passed its midpoint and the face is showing. */
  get faceUp(): boolean {
    return this.#flip >= 0.5;
  }

  get wildActive(): boolean {
    return this.#wildActive;
  }

  /** Whether the printed face is the side currently pointing at the camera. */
  get showingFace(): boolean {
    return this.#showingFace;
  }

  /* -------------------------------------------------- api */

  /**
   * Show a specific card. Wild status is taken from the card id via
   * `WILD_CARDS`, never from the rank — `jh` and `jd` are ordinary tens and get
   * no foil.
   */
  setFace(id: CardId): void {
    if (this.#cardId === id) return;
    this.#cardId = id;
    this.#shader.setFaceRect(getFaceUV(id));
    this.#shader.setNormalRect(getNormalUV(id));

    const wild = isWildCard(id);
    if (wild !== this.#wild) {
      this.#wild = wild;
      if (wild) this.#attachFoil();
      else this.#detachFoil();
      this.#shader.setWild(wild ? 1 : 0);
      this.#shader.setGloss(wild ? CARD_PHYSICS.wildGloss : 1);
    }
    this.#refreshVisibleSide();
  }

  /** Snap to a side with no animation. Equivalent to setting `flip` to 0 or 1. */
  setFaceUp(faceUp: boolean): void {
    this.flip = faceUp ? 1 : 0;
  }

  /**
   * Turn state, `0` = back showing, `1` = face showing.
   *
   * Tween it and the card rotates about its own vertical axis: the quad becomes
   * a real trapezoid with perspective-correct texturing, the highlight slides
   * across it, and face and back swap at exactly the half turn. The swap is
   * decided inside the shader from this one number, so nothing can be late with
   * it and there is no commit step.
   *
   * ```ts
   * gsap.to(card, { flip: 1, duration: motion.flip, ease: 'power2.inOut' });
   * ```
   */
  get flip(): number {
    return this.#flip;
  }

  set flip(progress: number) {
    const clamped = clamp(progress, 0, 1);
    if (clamped === this.#flip) return;
    this.#flip = clamped;
    this.#shader.setFlipAngle(clamped * Math.PI);
    this.#refreshVisibleSide();
  }

  /**
   * Virtual height above the table, in design units — the same scale
   * `SHADOW` and `motion/easing`'s `LIFT` use. It brightens and shifts the
   * specular highlight, magnifies the card slightly, widens the sliver of edge
   * the card shows, and lengthens, softens and weakens its shadow while
   * loosening its contact patch, all from this one number.
   */
  get lift(): number {
    return this.#lift;
  }

  set lift(value: number) {
    const next = Math.max(0, value);
    if (next === this.#lift) return;
    this.#lift = next;
    this.#shader.setLift(next);
  }

  /** Imperative alias for `lift`, per the module contract. */
  setLift(lift: number): void {
    this.lift = lift;
  }

  /**
   * Emissive rim, `0..1`. Lights the card from within along its bevel plus a
   * flat body glow — for wild reveals, 21 snaps and five-card cascades. Colour
   * comes from `setGlowColour`, and defaults to the wild's violet.
   */
  get glow(): number {
    return this.#glow;
  }

  set glow(value: number) {
    const next = clamp(value, 0, 4);
    if (next === this.#glow) return;
    this.#glow = next;
    this.#shader.setGlow(next);
  }

  /** Emissive colour, as a packed hex from `@/core/tokens`. */
  setGlowColour(hex: number): void {
    this.#shader.setGlowColour(hex);
  }

  /**
   * Whether the card narrows itself as it turns edge-on.
   *
   * On by default, because the turn belongs to the card: the shader's narrowing
   * comes with the perspective trapezoid and the moving highlight that no
   * outside `scale.x` tween can reproduce. Turn it **off** if an animator drives
   * `scale.x` for the squash instead, so the card is not narrowed twice — the
   * depth, the lighting and the sheen all still run.
   */
  get squashFromFlip(): boolean {
    return this.#squashFromFlip;
  }

  set squashFromFlip(enabled: boolean) {
    if (enabled === this.#squashFromFlip) return;
    this.#squashFromFlip = enabled;
    this.#shader.setFlipSquash(enabled);
  }

  /**
   * Hero state for a wild that is the active card: a stronger foil, a brighter
   * rim, and a reveal flare. A no-op on ordinary cards.
   */
  setWildActive(active: boolean): void {
    if (active && !this.#wild) {
      if (import.meta.env.DEV) {
        console.warn(
          `[card] setWildActive(true) on "${this.#cardId || 'none'}", which is not a wild. ` +
            'Only js and jc are wild; jh and jd are ordinary tens.',
        );
      }
      return;
    }
    if (this.#wildActive === active) return;
    this.#wildActive = active;
    const foil = this.#foil;
    if (!foil) return;
    foil.setBaseIntensity(
      active ? CARD_PHYSICS.wildActiveIntensity : CARD_PHYSICS.wildIdleIntensity,
    );
    if (active && this.#showingFace) foil.reveal();
  }

  /** Dim a card buried under others in a pile. 1 is fully lit. */
  setShade(value: number): void {
    this.#shader.setShade(value);
  }

  /**
   * Return the sprite to a pristine state. Everything a wild added — the foil
   * filter, the violet bias in the lit pass, the extra gloss, the animation
   * clocks — is torn down here, so a mesh that was J♠ is indistinguishable from
   * a fresh one when it comes back as a 4♦.
   */
  reset(): void {
    this.#detachFoil();
    this.#cardId = NO_CARD;
    this.#wild = false;
    this.#wildActive = false;
    this.#flip = 0;
    this.#lift = 0;
    this.#glow = 0;
    this.#showingFace = false;
    this.#squashFromFlip = true;
    this.#lastX = Number.NaN;
    this.#lastY = Number.NaN;
    this.#lastRotation = Number.NaN;
    this.#lastFlip = 0;
    this.#tiltX = 0;
    this.#tiltY = 0;
    this.#velX = 0;
    this.#velY = 0;
    this.#spin = 0;
    this.#flipRate = 0;

    this.#shader.resetUniforms();
    this.#shader.setBackRect(getBackUV());
    this.#shader.setFaceRect(getBackUV());
    this.#shader.setNormalRect(getNormalUV('back'));

    this.position.set(0, 0);
    this.rotation = 0;
    this.scale.set(1, 1);
    this.skew.set(0, 0);
    this.pivot.set(0, 0);
    this.alpha = 1;
    this.visible = true;
    this.zIndex = 0;
    this.blendMode = 'normal';
    // Anything the VFX layer hung on the card goes too — recycling has to leave
    // nothing of the previous card behind, cosmetic or otherwise.
    this.filters = [];

    this.shadow.reset();
  }

  /**
   * Per-frame work, driven by `updateCards(dt)`.
   *
   * @internal
   */
  updateFrame(dt: number): void {
    const x = this.x;
    const y = this.y;
    const rotation = this.rotation;

    // Travel lean, derived from actual motion rather than asked for by the
    // caller: a card that is moving leans into its travel, which shifts its
    // highlight out of step with its outline. That parallax is what sells
    // thickness when there is no third dimension to work with.
    const step = Math.max(dt, 1 / 240);
    const blend = 1 - Math.exp(-CARD_PHYSICS.tiltSmoothing * step);
    let dx = 0;
    let dy = 0;
    if (Number.isFinite(this.#lastX)) {
      dx = x - this.#lastX;
      dy = y - this.#lastY;
      const vx = dx / step;
      const vy = dy / step;
      const targetX = clamp(
        vx * CARD_PHYSICS.tiltPerVelocity,
        -CARD_PHYSICS.maxTilt,
        CARD_PHYSICS.maxTilt,
      );
      const targetY = clamp(
        vy * CARD_PHYSICS.tiltPerVelocity,
        -CARD_PHYSICS.maxTilt,
        CARD_PHYSICS.maxTilt,
      );
      this.#tiltX += (targetX - this.#tiltX) * blend;
      this.#tiltY += (targetY - this.#tiltY) * blend;
      // Velocity rides the same smoothing as the lean, so the travel highlight
      // decays with it instead of snapping off the frame a tween ends.
      this.#velX += (vx - this.#velX) * blend;
      this.#velY += (vy - this.#velY) * blend;
    }

    const spin = Number.isFinite(this.#lastRotation) ? (rotation - this.#lastRotation) / step : 0;
    this.#spin += (spin - this.#spin) * blend;
    this.#flipRate += ((this.#flip - this.#lastFlip) / step - this.#flipRate) * blend;

    this.#lastX = x;
    this.#lastY = y;
    this.#lastRotation = rotation;
    this.#lastFlip = this.#flip;

    // The lean is measured in table space; the shader wants it in card space.
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    this.#shader.setTilt(
      this.#tiltX * cos + this.#tiltY * sin,
      -this.#tiltX * sin + this.#tiltY * cos,
    );

    // This is the line that makes the table read as lit: every card hands the
    // shader its own position, so every card gets its own light vector.
    this.#shader.setCardPosition(x, y);
    this.#shader.setCardRotation(rotation);

    this.#syncMotionBlur(dx, dy, cos, sin);
    this.#syncSheen(cos, sin);
    this.#syncEdge(x, y);

    const foil = this.#foil;
    if (foil) {
      foil.setCardPosition(x, y);
      foil.setCardRotation(rotation);
      foil.setPixelScale(this.#worldScale());
      foil.setAlpha(this.alpha);
      foil.advance(dt);
    }

    this.shadow.visible = this.visible && this.alpha > 0.004;
    if (this.shadow.visible) {
      // When the shader owns the turn, the shadow has to narrow with it — a card
      // edge-on to the light casts a sliver, not a rectangle.
      const turn = this.#squashFromFlip ? Math.abs(Math.cos(this.#flip * Math.PI)) : 1;
      this.shadow.sync(
        x,
        y,
        rotation,
        this.scale.x * Math.max(turn, 0.04),
        this.scale.y,
        this.#lift,
        this.alpha,
      );
    }
  }

  /**
   * Take contact occlusion from a card drawn above this one.
   *
   * The occluder is handed over as a rounded box in this card's own frame, its
   * centre already pushed away from the light by the gap between the two on the
   * same `SHADOW.offsetScale` law the drop shadow uses. Zero gap is the contact
   * case: no offset, a tight radius and full strength.
   *
   * @internal — driven by `updateCards`.
   */
  applyOcclusionFrom(occluder: CardSprite): void {
    const options = CARD_FX.occlusion;
    const gap = Math.max(0, occluder.#lift - this.#lift);
    const decay = options.falloff > 0 ? Math.exp(-gap / options.falloff) : 0;
    const strength = options.strength * decay * occluder.alpha;
    if (strength <= 0.004) {
      this.#shader.clearOcclusion();
      return;
    }

    let ax = occluder.x;
    let ay = occluder.y;
    if (gap > 0) {
      const px = ax - LIGHT.x;
      const py = ay - LIGHT.y;
      const away = Math.hypot(px, py);
      if (away > 1e-4) {
        const push = (gap * SHADOW.offsetScale) / away;
        ax += px * push;
        ay += py * push;
      }
    }

    const rotation = this.rotation;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const sx = Math.abs(this.scale.x) > 1e-3 ? this.scale.x : 1e-3;
    const sy = Math.abs(this.scale.y) > 1e-3 ? this.scale.y : 1e-3;
    const dx = ax - this.x;
    const dy = ay - this.y;
    const rel = occluder.rotation - rotation;
    const mean = (Math.abs(sx) + Math.abs(sy)) * 0.5;

    this.#shader.setOcclusion(
      (dx * cos + dy * sin) / sx,
      (-dx * sin + dy * cos) / sy,
      Math.cos(rel),
      Math.sin(rel),
      (CARD.width / 2) * Math.abs(occluder.scale.x / sx),
      (CARD.height / 2) * Math.abs(occluder.scale.y / sy),
      (options.radius + gap * options.spread) / mean,
      strength,
    );
  }

  /** Drop any inter-card occlusion. @internal */
  clearOcclusion(): void {
    this.#shader.clearOcclusion();
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    untrackCard(this);
    this.#detachFoil();
    this.shadow.destroy({ children: true });
    super.destroy({ children: true });
    this.#shader.destroy();
  }

  /* -------------------------------------------------- internals */

  /**
   * Directional blur along this frame's travel.
   *
   * The vector is the raw per-frame delta rather than the smoothed velocity —
   * that is exactly what a one-frame shutter integrates — brought into the
   * card's own frame and capped, so a cut or a fast-forward that teleports a
   * card cannot turn it into a streak. Below the threshold the strength is zero
   * and the shader takes its single-tap path.
   */
  #syncMotionBlur(dx: number, dy: number, cos: number, sin: number): void {
    const options = CARD_FX.motionBlur;
    if (options.strength > 0 && options.threshold > 0 && !prefersReducedMotion()) {
      const travel = Math.hypot(dx, dy);
      if (travel > options.threshold) {
        const scale = Math.min(travel, options.maxLength) / travel;
        const ramp = Math.min(1, (travel - options.threshold) / options.threshold);
        const sx = Math.abs(this.scale.x) > 1e-3 ? this.scale.x : 1e-3;
        const sy = Math.abs(this.scale.y) > 1e-3 ? this.scale.y : 1e-3;
        this.#shader.setMotionBlur(
          ((dx * cos + dy * sin) * scale) / sx,
          ((-dx * sin + dy * cos) * scale) / sy,
          ramp * options.strength,
        );
        return;
      }
    }
    this.#shader.setMotionBlur(0, 0, 0);
  }

  /**
   * The travel highlight: an anisotropic lobe stretched along the direction the
   * card is moving, driven by travel, spin, turn rate and the lean those
   * produce. Every card catches the key light as it turns and travels; a wild
   * keeps only a fraction of it so it never doubles up with the foil sweep.
   */
  #syncSheen(cos: number, sin: number): void {
    const options = CARD_FX.sheen;
    if (!(options.gain > 0)) {
      this.#shader.setSheen(cos, sin, 0, options.anisotropy);
      return;
    }

    const speed = Math.hypot(this.#velX, this.#velY);
    let drive =
      Math.min(1, speed / Math.max(options.refSpeed, 1e-3)) * 0.7 +
      Math.min(1, Math.abs(this.#spin) / Math.max(options.refSpin, 1e-3)) * 0.3 +
      Math.min(1, Math.abs(this.#flipRate) / Math.max(options.refFlip, 1e-3)) * 0.55 +
      Math.min(1, Math.hypot(this.#tiltX, this.#tiltY) / CARD_PHYSICS.maxTilt) * 0.35;
    if (drive > 1) drive = 1;
    if (prefersReducedMotion()) drive *= 0.35;

    const gain = drive * options.gain * (this.#wild ? options.wildShare : 1);
    // A card that is barely moving has no meaningful travel direction, so the
    // lobe falls back to the card's own long axis and the turn drives it.
    const along = speed > 1;
    this.#shader.setSheen(
      along ? this.#velX / speed : cos,
      along ? this.#velY / speed : sin,
      gain,
      options.anisotropy,
    );
  }

  /**
   * The card's own thickness. The sliver stands on the side facing away from
   * `LIGHT` — the same side the drop shadow is thrown to — and widens with lift,
   * so a card in the air shows more of its edge than one lying flat.
   */
  #syncEdge(x: number, y: number): void {
    const options = CARD_FX.edge;
    if (!(options.width > 0) || !(options.gain > 0)) {
      this.#shader.setEdge(0, 0, 0, 0);
      return;
    }
    // Which side face of a card you can see is a CAMERA fact, not a light one.
    //
    // This used to take the direction from LIGHT, which put the visible edge on
    // the bottom-right of every card on the table — including the stock, which
    // sits left of centre and should show its *left* edge. The eye is centred
    // over the table (see VIEW_PARALLAX / VIEW_DIST in the shader), so the
    // visible edge is simply the direction away from the table centre.
    let dx = x - DESIGN.width / 2;
    let dy = y - DESIGN.height / 2;
    const distance = Math.hypot(dx, dy);
    if (distance > 1e-4) {
      dx /= distance;
      dy /= distance;
    } else {
      dx = 0;
      dy = 1;
    }

    // Width is geometric, not constant.
    //
    // The projected width of the cut face is thickness * |offset from centre| *
    // VIEW_PARALLAX / VIEW_DIST. At the outer piles that is ~0.73 design units
    // and at the centre pile ~0.19 — a card dead centre of the table shows
    // essentially no edge at all, and *that variation is the cue*. A flat 2.2
    // everywhere was 3x to 11x exaggerated and identical across the table.
    const geometric =
      (CARD_PHYSICS.thickness * distance * CARD_SHADING.viewParallax) /
      CARD_SHADING.viewDistance;
    const width = Math.min(
      options.maxWidth,
      options.width + geometric * 1.8 + this.#lift * options.liftGain,
    );
    this.#shader.setEdge(dx, dy, width, options.gain);
  }

  #worldScale(): number {
    const m = this.worldTransform;
    const scale = Math.hypot(m.a, m.b);
    return scale > 1e-5 ? scale : 1;
  }

  #attachFoil(): void {
    if (this.#foil) return;
    const foil = new FoilFilter();
    foil.setBaseIntensity(CARD_PHYSICS.wildIdleIntensity);
    this.#foil = foil;
    this.#mesh.filters = [foil];
  }

  #detachFoil(): void {
    const foil = this.#foil;
    if (!foil) return;
    this.#mesh.filters = [];
    this.#foil = null;
    foil.reset();
    foil.destroy();
  }

  /**
   * Recompute which printed side faces the camera, using the same rule the
   * shader uses, and fire the reveal flare on the rising edge for a wild.
   */
  #refreshVisibleSide(): void {
    const showing = this.#flip >= 0.5;
    if (showing === this.#showingFace) return;
    this.#showingFace = showing;
    if (showing && this.#wild && !prefersReducedMotion()) {
      this.#foil?.reveal();
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/* ------------------------------------------------------------------ *
 * Frame update
 * ------------------------------------------------------------------ */

/**
 * How far apart two card centres can be and still overlap, with slack for
 * rotation and for the scale a landing card overshoots to.
 */
const OCCLUDE_REACH_X = CARD.width * 1.3;
const OCCLUDE_REACH_Y = CARD.height * 1.3;

/**
 * Give every card the nearest card drawn above it, as its occluder.
 *
 * "Nearest above" rather than "all above" because in a downward fan every card
 * above overlaps, but only the one directly on top still has a visible edge
 * crossing this card — the rest are hidden behind it. The same rule picks up a
 * card in flight passing over a pile, since nothing is drawn between them, which
 * is how a flying card's shadow reaches the cards it passes over at all: the
 * shadow layer is under every card, so a cast shadow alone never could.
 *
 * O(n²) over the handful of cards on the table, integer compare first, no
 * allocation.
 */
function updateOcclusion(): void {
  const count = liveList.length;
  const options = CARD_FX.occlusion;

  if (!(options.strength > 0)) {
    for (let i = 0; i < count; i++) liveList[i].clearOcclusion();
    return;
  }

  for (let i = 0; i < count; i++) {
    const receiver = liveList[i];
    if (!receiver.visible || receiver.alpha < 0.02) {
      receiver.clearOcclusion();
      continue;
    }

    const rz = receiver.zIndex;
    const rx = receiver.x;
    const ry = receiver.y;
    let best: CardSprite | null = null;
    let bestZ = Number.POSITIVE_INFINITY;

    for (let j = 0; j < count; j++) {
      if (j === i) continue;
      const other = liveList[j];
      const oz = other.zIndex;
      if (oz <= rz || oz >= bestZ) continue;
      if (!other.visible || other.alpha < options.minAlpha) continue;
      if (Math.abs(other.x - rx) > OCCLUDE_REACH_X) continue;
      if (Math.abs(other.y - ry) > OCCLUDE_REACH_Y) continue;
      best = other;
      bestZ = oz;
    }

    if (best === null) receiver.clearOcclusion();
    else receiver.applyOcclusionFrom(best);
  }
}

/**
 * Advance every live card by `dt` seconds.
 *
 * **This must be called once per frame from the render loop.** It feeds each
 * card's position into its own light vector, advances the foil sweep and the
 * reveal envelopes, derives the travel lean, the motion blur and the travel
 * sheen from real motion, resolves which card is occluding which, and keeps
 * every shadow in agreement with the shading. Pooled (released) cards are
 * skipped.
 */
export function updateCards(dt: number): void {
  const step = Number.isFinite(dt) ? clamp(dt, 0, 0.1) : 0;
  for (let i = 0; i < liveList.length; i++) liveList[i].updateFrame(step);
  updateOcclusion();
}

/** How many cards are currently being ticked. Useful for debug overlays. */
export function liveCardCount(): number {
  return liveCards.size;
}

/* ------------------------------------------------------------------ *
 * Pool
 * ------------------------------------------------------------------ */

export interface CardPoolOptions {
  /** Layer the card meshes are parented to on acquire. */
  cardLayer?: Container | null;
  /** Layer the drop shadows are parented to on acquire. */
  shadowLayer?: Container | null;
  /** Sprites created up front. A round never needs more than a handful. */
  prewarm?: number;
}

/**
 * Hands out card sprites and takes them back, keyed by card id.
 *
 * Recycling is the one place a rendering bug can become a *gameplay* bug: if a
 * mesh that was J♠ came back as a 4♦ still wearing its foil, the player would
 * read a wild that is not there. `acquire()` therefore resets unconditionally
 * and, in dev builds, verifies the wild state really is gone before handing the
 * sprite over.
 */
export class CardPool {
  /** Sprites currently being drawn invisibly to warm the GPU. See {@link warm}. */
  readonly #warm: CardSprite[] = [];

  readonly #free: CardSprite[] = [];
  /** Every sprite currently checked out, whether or not it has a card id. */
  readonly #active = new Set<CardSprite>();
  /** Fast lookup by card id, for `get()`. */
  readonly #byId = new Map<CardId, CardSprite>();
  readonly #cardLayer: Container | null;
  readonly #shadowLayer: Container | null;

  constructor(options: CardPoolOptions = {}) {
    this.#cardLayer = options.cardLayer ?? null;
    this.#shadowLayer = options.shadowLayer ?? null;
    const prewarm = options.prewarm ?? 0;
    for (let i = 0; i < prewarm; i++) {
      const sprite = new CardSprite();
      sprite.reset();
      sprite.visible = false;
      untrackCard(sprite);
      this.#free.push(sprite);
    }
  }

  /**
   * Draw a few cards once, invisibly, so the first real ones are not the frames
   * that pay for them.
   *
   * `prewarm` already builds sprites up front, but it hides them — and a hidden
   * sprite costs nothing, which is the problem. Nothing about a `CardSprite`
   * reaches the GPU until it is actually drawn: the lighting program is not
   * compiled, the mesh geometry and the per-sprite uniform buffers are not
   * uploaded, and the atlas pages the card samples are not resident. All of it
   * lands during the opening deal instead, which is why the first cards drag
   * and the tenth does not.
   *
   * The wild is warmed deliberately and separately. A wild attaches a
   * `FoilFilter` the first time one is dealt — a *second* program, compiled at
   * the moment a wild first appears, which is exactly the beat the foil sweep
   * is supposed to sell.
   *
   * @param wild - a wild card id, so the foil program is compiled too.
   */
  warm(cards: readonly CardId[], wild: CardId): void {
    if (this.#warm.length > 0) return;

    // Every sprite `prewarm` built, not a token few. The lighting *program* is
    // compiled once and shared, but each sprite owns its own shader instance
    // and its own uniform buffers, and those upload on the frame that sprite is
    // first drawn. That per-sprite cost is what makes the deal keep getting
    // smoother over the first dozen cards instead of being smooth immediately.
    const count = Math.min(Math.max(this.#free.length, 1), cards.length);
    const ids = [...cards.slice(0, count), wild];

    ids.forEach((id, i) => {
      // `acquire` parents it to the card layer already.
      const sprite = this.acquire(id, true);
      // On screen, because an off-screen sprite can be culled and a culled
      // sprite draws nothing — but at an alpha that cannot be seen. Zero would
      // not do: fully transparent is also skippable.
      sprite.position.set(DESIGN.width / 2, DESIGN.height / 2);
      sprite.alpha = WARM_ALPHA;
      sprite.zIndex = -1000;

      // A card in flight is not one state, it is a sweep through several, and
      // each one lights up a different piece of the shadow rig. At rest the
      // contact patch is drawn and the cast shadow is tight; in the air the
      // patch is gone and the soft shadow takes over. Warming only the resting
      // pose leaves the airborne half to upload on the first card thrown —
      // which is the animation that felt worst.
      sprite.lift = (i / Math.max(1, ids.length - 1)) * LIFT.hero;
      // Both printed sides, because they are different regions of the atlas.
      sprite.setFaceUp(i % 2 === 0);

      this.#warm.push(sprite);
    });
  }

  /** Undo {@link warm} and hand the sprites back. */
  cool(): void {
    for (const sprite of this.#warm) {
      // Only if it is still ours. A round starting inside the warm-up window
      // would have released these already, and restoring the alpha on a sprite
      // that has since been dealt to a pile would blank a card in play.
      if (!this.#active.has(sprite)) continue;
      sprite.alpha = 1;
      this.release(sprite);
    }
    this.#warm.length = 0;
  }

  get activeCount(): number {
    return this.#active.size;
  }

  get pooledCount(): number {
    return this.#free.length;
  }

  /**
   * The live sprite showing `id`, checking one out of the pool if there is not
   * one already. Repeated calls for the same card return the same sprite.
   */
  get(id: CardId): CardSprite {
    return this.#byId.get(id) ?? this.acquire(id);
  }

  /** Take a sprite, optionally already showing `id`. */
  acquire(id?: CardId, faceUp = false): CardSprite {
    const sprite = this.#free.pop() ?? new CardSprite();
    sprite.reset();

    if (import.meta.env.DEV) assertRecycledClean(sprite);

    trackCard(sprite);
    this.#active.add(sprite);

    if (id !== undefined) {
      sprite.setFace(id);
      this.#byId.set(id, sprite);
    }
    sprite.setFaceUp(faceUp);

    this.#cardLayer?.addChild(sprite);
    this.#shadowLayer?.addChild(sprite.shadow);
    return sprite;
  }

  /** Hand back the sprite currently showing `id`. */
  recycle(id: CardId): void {
    const sprite = this.#byId.get(id);
    if (sprite) this.release(sprite);
  }

  /** Give a sprite back. It stops being ticked and is hidden. */
  release(sprite: CardSprite): void {
    if (!this.#active.delete(sprite)) return;
    // The id has to be read before reset() wipes it.
    const id = sprite.cardId;
    if (id !== NO_CARD && this.#byId.get(id) === sprite) this.#byId.delete(id);

    sprite.reset();
    sprite.visible = false;
    sprite.shadow.visible = false;
    sprite.removeFromParent();
    sprite.shadow.removeFromParent();
    untrackCard(sprite);
    this.#free.push(sprite);
  }

  releaseAll(): void {
    for (const sprite of [...this.#active]) this.release(sprite);
    this.#byId.clear();
    // These have just been handed back with everything else; `cool` must not
    // reach for them again.
    this.#warm.length = 0;
  }

  destroy(): void {
    this.releaseAll();
    for (const sprite of this.#free) sprite.destroy();
    this.#free.length = 0;
  }
}

/**
 * The recycle check, run in dev on every acquire. A sprite that was a wild must
 * come back with no foil filter, no wild flag and no card id — anything else
 * would put a phantom wild on the table.
 */
function assertRecycledClean(sprite: CardSprite): void {
  const mesh = sprite.children[0];
  const filters = mesh?.filters;
  const filterCount = Array.isArray(filters) ? filters.length : filters ? 1 : 0;
  if (
    sprite.isWild ||
    sprite.wildActive ||
    sprite.cardId !== NO_CARD ||
    sprite.glow !== 0 ||
    filterCount > 0
  ) {
    console.error('[card] recycled sprite still carries wild state', {
      cardId: sprite.cardId,
      isWild: sprite.isWild,
      wildActive: sprite.wildActive,
      glow: sprite.glow,
      filterCount,
    });
  }
}

/** Re-exported so callers do not have to reach into two modules for one rule. */
export { isWildCard } from '@/pixi/filters/foil';

/** Design-space geometry a caller may want when placing cards. */
export const CARD_SIZE = { width: CARD.width, height: CARD.height } as const;

/** Centre of the design space — the default resting position of a fresh card. */
export const DESIGN_CENTRE = { x: DESIGN.width / 2, y: DESIGN.height / 2 } as const;

/** Emissive colours the motion layer is expected to reach for. */
export const GLOW_COLOURS = {
  wild: palette.violetLight,
  twentyOne: palette.mint,
  fiveCard: palette.gold,
  bust: palette.rose,
} as const;
